/**
 * A SIGNED-IN THREAD'S LOCAL COPY IS ITS NEWEST WINDOW (2026-10-24, Miles: switching threads "has been failing
 * and or at least taking a while to load").
 *
 * The copy this browser keeps of a thread (`prest0_thread_msgs__…`) held every message the pane had ever
 * loaded, and every change in the open thread saved all of it again, on the main thread. On the busiest
 * account a shared room's whole history was in it (1,043 rows, about 4 million characters): one save held the
 * page for 334 ms at the median and up to 1.2 s, up to 5,990 saves on one page (the chat health record, 7
 * days), and that one copy filled the browser's storage bin, so it was the first thing cleared once the person
 * moved to another thread, and coming back meant reading the server again.
 *
 * So the copy holds the thread's newest LOCAL_COPY_MESSAGES. Nothing is lost: the per-message store on the
 * server is the whole history, and the shared transcript document holds the newest MAX_THREAD_MESSAGES, which
 * this window always covers (pinned by test). Older history is read a page at a time when the reader scrolls
 * to it. A guest's copy stays whole: until it is adopted at sign-in it can be the only copy.
 *
 * A window proves what it contains, never what it lacks (the same rule as storedSnapshotAdoption.ts): a
 * thread's message count, or whether the person has written in it, read from what may be a window can rise
 * but never fall, and a pane that opens on one offers the older pages at once. Any list SMALLEST_THREAD_WINDOW
 * long may be one: this device's own window, or the server's window of a thread first opened here (which,
 * once stored, used to reach no older page after a reload, and set the thread's count to 100).
 *
 * Imports nothing: threadPersist applies the window and useChatThreads reads it, and eleven chat-isolation
 * suites mock `../threadPersist` wholesale (the constant lives where a mock cannot erase it).
 */

/** The most messages a signed-in thread's local copy holds: its newest. */
export const LOCAL_COPY_MESSAGES = 150;

/**
 * The smallest window a thread arrives in: a thread opened on a device with no copy of it reads its newest
 * this many from the server (chatThreadMessagesRepository.THREAD_OPEN_WINDOW_SIZE, pinned equal by test), and
 * that window is what the device then stores. A list at least this long may be a window of a longer thread.
 */
export const SMALLEST_THREAD_WINDOW = 100;

/** The newest LOCAL_COPY_MESSAGES of a thread's messages (the same list when it already fits). */
export function newestLocalWindow<T>(messages: T[]): T[] {
  return messages.length > LOCAL_COPY_MESSAGES ? messages.slice(messages.length - LOCAL_COPY_MESSAGES) : messages;
}

/** Can a list this long be the window of a longer thread? */
export function mayBeLocalWindow(length: number): boolean {
  return length >= SMALLEST_THREAD_WINDOW;
}

/** A message count read from what may be a window: never below the count already known. */
export function countReadThroughWindow(known: number | undefined, counted: number, windowed: boolean): number {
  if (!windowed) return counted;
  return typeof known === 'number' && Number.isFinite(known) && known > counted ? known : counted;
}

/** "The person has written here", read from what may be a window: once known, it stays known. */
export function userContentReadThroughWindow(known: boolean | undefined, found: boolean, windowed: boolean): boolean {
  return windowed ? known === true || found : found;
}

/**
 * The newest message time in a list, in milliseconds (a stored copy carries ISO strings, a live list Dates);
 * 0 when none reads. Two copies of one thread are compared by this, never by length: windows of one length can
 * end at different messages.
 */
export function newestMessageTime(messages: readonly unknown[] | null | undefined): number {
  let newest = 0;
  for (const m of messages ?? []) {
    const raw = (m as { timestamp?: unknown } | null)?.timestamp;
    const ms = raw instanceof Date ? raw.getTime() : typeof raw === 'string' || typeof raw === 'number' ? new Date(raw).getTime() : NaN;
    if (Number.isFinite(ms) && ms > newest) newest = ms;
  }
  return newest;
}
